import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import { ValuationChart } from "../components/ValuationChart";
import { useFetchApi } from "../../../../../../hooks/useFetchApi";

const { Title, Text } = Typography;

interface ValuationSnapshotProps {
  sectorPeers: any[];
  baseCurrency: string;
  selectedStock: any;
}

const generateBuckets = (
  values: number[],
  companyValue: number,
  numBuckets = 7,
) => {
  const validValues = values
    .filter((v) => v != null && isFinite(v) && v > 0)
    .sort((a, b) => a - b);

  if (!validValues.length) return [];

  // Quantile edges: numBuckets+1 boundaries carved so each bucket
  // gets roughly the same number of data points, instead of equal-width
  // ranges (which collapse when the data is skewed by outliers).
  const getQuantile = (p: number) => {
    const idx = p * (validValues.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return validValues[lo];
    return validValues[lo] + (validValues[hi] - validValues[lo]) * (idx - lo);
  };

  const edges = Array.from({ length: numBuckets + 1 }, (_, i) =>
    getQuantile(i / numBuckets),
  );

  // Build descending (high → low) so index 0 = highest, renders at top in ApexCharts
  return Array.from({ length: numBuckets }, (_, i) => {
    const bucketIdx = numBuckets - 1 - i;
    const low = edges[bucketIdx];
    const high = edges[bucketIdx + 1];
    const isTopBucket = bucketIdx === numBuckets - 1;
    const count = validValues.filter((v) =>
      isTopBucket ? v >= low && v <= high : v >= low && v < high,
    ).length;
    return {
      label: `${low.toFixed(2)}-${high.toFixed(2)}`,
      low: parseFloat(low.toFixed(2)),
      high: parseFloat(high.toFixed(2)),
      count,
      isTopBucket,
    };
  });
};

const getRank = (values: number[], companyValue: number, ascending = true) => {
  const sorted = [...values]
    .filter((v) => v != null && isFinite(v) && v > 0)
    .sort((a, b) => (ascending ? a - b : b - a));
  const rank = sorted.findIndex((v) => Math.abs(v - companyValue) < 0.001);
  return rank === -1 ? sorted.length : rank + 1;
};

const getMedian = (values: number[]) => {
  const sorted = [...values]
    .filter((v) => v != null && isFinite(v) && v > 0)
    .sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

export const ValuationSnapshot: React.FC<ValuationSnapshotProps> = ({
  selectedStock,
  sectorPeers,
  baseCurrency,
}) => {
  const [peersData, setPeersData] = useState<any[]>([]);
  const [sectorName, setSectorName] = useState<string | null>(null);

  const { response, loading, execute } = useFetchApi("/api/peers", {}, "POST");

  useEffect(() => {
    if (!sectorPeers?.length) return;
    execute({
      stocks: sectorPeers.map((s) => ({ id: s.id })),
      baseCurrency,
    });
  }, [sectorPeers, baseCurrency]);

  useEffect(() => {
    if (!response) return;
    console.log(response);
    setPeersData(response.data ?? []);
    setSectorName(response.sectorName);
  }, [response]);

  const peValues = peersData
    .map((p) => p?.data?.dynamic?.PELTM)
    .filter((v) => v != null && isFinite(v) && v > 0);

  const pbValues = peersData
    .map((p) => p?.data?.dynamic?.priceToBookValue)
    .filter((v) => v != null && isFinite(v) && v > 0);

  const roeValues = peersData
    .map((p) => p?.data?.dynamic?.returnOnEquityLTM)
    .filter((v) => v != null && isFinite(v) && v > 0);

  const currentStockData = peersData.find((p) => p.id === selectedStock?.id);
  const companyPE = currentStockData?.data?.dynamic?.PELTM ?? 0;
  const companyPB = currentStockData?.data?.dynamic?.priceToBookValue ?? 0;
  const companyROE = currentStockData?.data?.dynamic?.returnOnEquityLTM ?? 0;
  const sectorPEBySector = currentStockData?.data?.dynamic?.PEBySector ?? 0;

  const sectorPE = getMedian(peValues);
  const sectorPB = getMedian(pbValues);
  const sectorROE = getMedian(roeValues);

  const totalStocks = peersData.length;
  const petotalStocks = peValues.length;

  const peProps = {
    sectorPE: sectorPEBySector,
    companyPE,
    companyRank: getRank(peValues, companyPE, true),
    totalStocks,
    peDeviation: parseFloat((companyPE - sectorPEBySector).toFixed(2)),
    buckets: generateBuckets(peValues, companyPE),
  };

  const pbProps = {
    sectorPE: sectorPB,
    companyPE: companyPB,
    companyRank: getRank(pbValues, companyPB, true),
    totalStocks,
    peDeviation: parseFloat((companyPB - sectorPB).toFixed(2)),
    buckets: generateBuckets(pbValues, companyPB),
  };

  const roeProps = {
    sectorPE: sectorROE,
    companyPE: companyROE,
    companyRank: getRank(roeValues, companyROE, false),
    totalStocks,
    peDeviation: parseFloat((companyROE - sectorROE).toFixed(2)),
    buckets: generateBuckets(roeValues, companyROE),
  };

  if (loading) {
    return (
      <div>
        <Title level={5} className="mb-1">
          Valuation Snapshot
        </Title>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-lg p-4 h-64 animate-pulse bg-gray-50"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Title level={5} className="mb-1">
        Valuation Snapshot
      </Title>
      <Text className="text-xs text-gray-400 block mb-4">
        {totalStocks} stocks in {sectorName ?? "sector"} sector
      </Text>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ValuationChart
          {...peProps}
          label="P / E"
          sectorLabel="SECTOR P/E"
          isRed={sectorPEBySector > companyPE}
        />
        <ValuationChart
          {...pbProps}
          label="P / B"
          sectorLabel="SECTOR P/B"
          isRed={sectorPB > companyPB}
        />
        <ValuationChart
          {...roeProps}
          label="ROE"
          sectorLabel="SECTOR MEDIAN"
          isPercentage
        />
      </div>
    </div>
  );
};
